import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import TechnologiesByIndustry from '@/components/feature/TechnologiesByIndustry';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';
import { industriesDetail } from '@/mocks/industriesDetail';
import { useState, useEffect } from 'react';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const industryData = industriesDetail.find((i) => i.id === 'logistics')!;

/* ── Hero ── */
function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-20 md:pt-40 md:pb-32 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20logistics%20technology%20background%20with%20glowing%20route%20lines%20connecting%20nodes%20on%20a%20digital%20map%2C%20deep%20navy%20and%20golden%20yellow%20color%20palette%20with%20amber%20accent%20highlights%2C%20futuristic%20supply%20chain%20network%20visualization%2C%20soft%20bokeh%20light%20particles%2C%20professional%20minimal%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay&width=1600&height=900&seq=logistics-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-yellow-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-truck-line text-yellow-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Industry Focus</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Engineering Talent for
            <br />
            <span className="text-yellow-400">Logistics & Supply Chain</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl"
          >
            Build fleet management platforms, warehouse automation systems, and AI-powered route optimization with engineers who have reduced delivery costs and idle time for global logistics networks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-start gap-4"
          >
            <Link
              to="/talent"
              className="px-8 py-4 bg-yellow-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-yellow-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse Logistics Engineers
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/case-studies"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Logistics Case Studies
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── Trust Badges ── */
function LogisticsTrustBadges() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const badges = [
    { icon: 'ri-shield-check-line', label: 'ISO 27001', desc: 'Information security certified' },
    { icon: 'ri-lock-star-line', label: 'SOC 2 Ready', desc: 'Trust services certified' },
    { icon: 'ri-map-pin-line', label: 'GDPR Compliant', desc: 'EU data protection' },
    { icon: 'ri-cloud-line', label: 'AWS Partner', desc: 'Cloud-native infrastructure' },
  ];

  return (
    <section className="py-8 md:py-12 bg-navy-900 border-t border-white/10" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition }}
          className="flex flex-wrap items-center justify-center gap-6 md:gap-10"
        >
          {badges.map((badge, i) => (
            <motion.div
              key={badge.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, ...springTransition }}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl">
                <i className={`${badge.icon} text-lg text-yellow-400`}></i>
              </div>
              <div>
                <div className="text-sm font-bold text-white">{badge.label}</div>
                <div className="text-xs text-white/50">{badge.desc}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ── Challenges ── */
function LogisticsChallenges() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const challenges = [
    {
      icon: 'ri-route-line',
      title: 'Route Optimization at Scale',
      desc: 'Static routing wastes fuel and driver hours. Real-time dynamic routing with traffic, weather, and delivery-window constraints is a complex optimization problem.',
      stat: '91%',
      statLabel: 'fleet utilization achieved',
    },
    {
      icon: 'ri-building-4-line',
      title: 'Warehouse Automation',
      desc: 'Modern warehouses require WMS integrations, barcode scanning, pick-path optimization, and real-time inventory tracking across multiple facilities.',
      stat: '40%',
      statLabel: 'pick efficiency improvement',
    },
    {
      icon: 'ri-radar-line',
      title: 'Real-Time Fleet Visibility',
      desc: 'Dispatchers need live GPS tracking, ETA predictions, and exception alerts. Building telemetry pipelines that scale to thousands of vehicles is non-trivial.',
      stat: '99.7%',
      statLabel: 'tracking uptime',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Demand Forecasting',
      desc: 'Accurate demand prediction prevents stockouts and reduces carrying costs. ML models must ingest seasonal patterns, promotions, and external signals.',
      stat: '94%',
      statLabel: 'forecast accuracy',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-alert-line text-yellow-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Logistics Hiring Is Hard</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            The Logistics Engineering
            <span className="text-yellow-600"> Challenge</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Supply chain platforms face unique technical constraints that generalist developers do not understand. Here is why the wrong hire costs more than no hire.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {challenges.map((c, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-yellow-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${c.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-yellow-600">{c.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{c.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{c.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Solutions ── */
function LogisticsSolutions() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const solutions = [
    {
      title: 'Route Optimization Engineers',
      desc: 'Build ML-powered routing engines that dynamically optimize for fuel cost, delivery windows, vehicle capacity, and driver hours in real time.',
      icon: 'ri-route-line',
      roles: ['Python / ML Engineers', 'Algorithm Specialists', 'GIS Developers'],
    },
    {
      title: 'Fleet Management Teams',
      desc: 'Develop real-time GPS tracking, driver mobile apps, and dispatch dashboards with live ETA predictions and exception handling.',
      icon: 'ri-truck-line',
      roles: ['React Native Devs', 'Backend Engineers', 'Real-Time Systems Devs'],
    },
    {
      title: 'Warehouse Systems Integrators',
      desc: 'Integrate WMS, barcode scanning, pick-path optimization, and conveyor control systems for modern automated warehouses.',
      icon: 'ri-building-4-line',
      roles: ['Backend Engineers', 'IoT Developers', 'Integration Specialists'],
    },
    {
      title: 'Supply Chain Data Engineers',
      desc: 'Build demand forecasting models, inventory optimization pipelines, and end-to-end supply chain visibility platforms.',
      icon: 'ri-bar-chart-box-line',
      roles: ['Data Engineers', 'ML Engineers', 'Analytics Engineers'],
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-user-star-line text-yellow-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">How We Help</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Logistics Talent,
            <span className="text-yellow-400"> Deployed</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-3xl mx-auto px-2"
          >
            We do not send generalists. We match engineers who have built the exact supply chain systems you are trying to scale.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {solutions.map((s, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-white/5 backdrop-blur-md rounded-2xl p-6 md:p-7 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${s.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed mb-4">{s.desc}</p>
              <div className="flex flex-wrap gap-2">
                {s.roles.map((role) => (
                  <span key={role} className="px-2.5 py-1 bg-white/10 rounded-lg text-xs font-medium text-white/70 border border-white/10">
                    {role}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study ── */
function LogisticsCaseStudy() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-folder-chart-line text-yellow-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Client Success</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Logistics Case
            <span className="text-yellow-600"> Study</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, ...springTransition }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center"
        >
          <div className="rounded-2xl overflow-hidden border border-gray-100">
            <img
              src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28industryData.caseStudy.imageQuery%29%7D&width=800&height=500&seq=logistics-cs-1&orientation=landscape`}
              alt="Logistics Platform"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-yellow-50 border border-yellow-200 rounded-full text-xs font-bold text-yellow-700">
                Logistics & Supply Chain
              </span>
              <span className="text-sm text-gray-400">{industryData.caseStudy.duration}</span>
              <span className="text-sm text-gray-400">{industryData.caseStudy.engineers} engineers</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
              {industryData.caseStudy.client} — {industryData.caseStudy.project}
            </h3>
            <div className="mb-4">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Challenge</span>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">{industryData.caseStudy.challenge}</p>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-yellow-600 uppercase tracking-wider">Outcome</span>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">{industryData.caseStudy.outcome}</p>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-yellow-600 hover:text-yellow-700 transition-colors cursor-pointer group"
            >
              Read full case study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Talent Stats ── */
function LogisticsTalentStats() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { value: industryData.talentPool.totalAvailable.toString(), label: 'Logistics Engineers Available', icon: 'ri-user-search-line' },
    { value: industryData.talentPool.avgExperience, label: 'Average Experience', icon: 'ri-medal-line' },
    { value: industryData.talentPool.roles.length.toString(), label: 'Role Types', icon: 'ri-group-line' },
    { value: '48h', label: 'Avg. Match Time', icon: 'ri-time-line' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Available
            <span className="text-yellow-600"> Logistics Talent</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
          {stats.map((s, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.2 + index * 0.08, ...springTransition }}
              className="bg-white rounded-2xl p-6 md:p-7 border border-gray-100 text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 rounded-xl mx-auto mb-3">
                <i className={`${s.icon} text-xl text-yellow-600`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-1">{s.value}</div>
              <div className="text-xs md:text-sm text-gray-500">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, ...springTransition }}
          className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8"
        >
          <h3 className="text-lg font-bold text-navy-900 mb-4">Top Logistics Roles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {industryData.talentPool.roles.map((role) => (
              <div key={role.title} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">{role.title}</span>
                <span className="text-sm font-bold text-navy-900">{role.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Top Skills</h4>
            <div className="flex flex-wrap gap-2">
              {industryData.talentPool.topSkills.map((skill) => (
                <span key={skill} className="px-3 py-1.5 bg-yellow-50 border border-yellow-100 rounded-lg text-xs font-semibold text-yellow-700">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── FAQ ── */
function LogisticsFAQ() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: 'Do your engineers have experience with route optimization algorithms?',
      a: 'Yes. Our logistics engineers have built and deployed VRP (Vehicle Routing Problem) solvers, genetic algorithms, and reinforcement learning models for dynamic route optimization. They have experience with OSRM, Google Maps API, and proprietary routing engines.',
    },
    {
      q: 'Can you provide engineers with warehouse management system (WMS) integration experience?',
      a: 'Absolutely. We have engineers who have integrated with SAP EWM, Manhattan Associates, Blue Yonder, and custom WMS platforms. They understand barcode scanning, ASN/EDI flows, and pick-path optimization.',
    },
    {
      q: 'How quickly can a logistics engineer ramp up on our fleet management platform?',
      a: 'Most logistics engineers are productive within 3-5 days. They come experienced with GPS telemetry, MQTT protocols, geofencing, and driver mobile app development using React Native and Flutter.',
    },
    {
      q: 'Do you offer engineers with IoT and sensor integration experience?',
      a: 'Yes. We have IoT specialists who have built telemetry pipelines using MQTT, LoRaWAN, and cellular modems. They can integrate temperature sensors, door sensors, and fuel-level monitors into your fleet management platform.',
    },
    {
      q: 'What is the minimum engagement length for logistics talent?',
      a: 'Engagements start at 3 months with a 2-week notice period. Most clients extend indefinitely. We also offer surge capacity for peak seasons and dedicated pods for long-term platform development.',
    },
  ];

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqs]);

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-questionnaire-line text-yellow-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Common Questions</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4"
          >
            Logistics Hiring
            <span className="text-yellow-600"> FAQ</span>
          </motion.h2>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="rounded-2xl border border-gray-100 bg-gray-50 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer group"
              >
                <span className="text-sm md:text-base font-semibold text-navy-900 group-hover:text-yellow-600 transition-colors pr-4">
                  {faq.q}
                </span>
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-gray-500"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA ── */
function LogisticsCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-yellow-500/15 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-rocket-line text-yellow-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Ready to Optimize?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Hire Logistics Engineers
            <span className="text-yellow-400"> in 48 Hours</span>
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Get pre-vetted engineers with supply chain and logistics platform experience. No generic recruiters. No resume spam. Just domain-matched talent.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-yellow-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-yellow-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse Logistics Talent
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              How It Works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
export default function LogisticsLandingPage() {
  return (
    <PageLayout title="Logistics Engineering Talent — Supply Chain & Route Optimization" description="Hire pre-vetted logistics engineers with route optimization, WMS integration, fleet management, and supply chain platform experience. IoT and MQTT specialists. Deploy in 48 hours." keywords="logistics engineers for hire, route optimization developers, WMS integration engineers, fleet management software developers, supply chain engineers, IoT logistics developers, warehouse automation engineers, OSRM specialists">
      <PageHero />
      <LogisticsTrustBadges />
      <LogisticsChallenges />
      <TechnologiesByIndustry highlightIndustry="logistics" />
      <LogisticsSolutions />
      <LogisticsCaseStudy />
      <LogisticsTalentStats />
      <HowWeVetEngineers
        accentColor="yellow"
        heading="How We Vet Logistics Engineers"
        specialtyNote="For logistics clients, we additionally verify routing algorithm experience, GIS and mapping API knowledge, and IoT sensor integration skills before any placement."
      />
      <LogisticsFAQ />
      <LogisticsCTA />
    </PageLayout>
  );
}